import React from 'react';
import {
  getPortfolioTotals, formatCurrency, formatAED, STATUS_CONFIG,
  getProjectRawShared, getProjectTotalShared,
  monthsBetween, formatDate, getTeamSize,
} from '../utils';

function KpiCard({ icon, label, value, sub, sub2, color }) {
  return (
    <div style={{
      background: '#0f172a', border: `1px solid ${color}33`,
      borderTop: `3px solid ${color}`, borderRadius: 12,
      padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: 4,
    }}>
      <div style={{ fontSize: 22 }}>{icon}</div>
      <div style={{ fontSize: 10, fontWeight: 700, color: '#475569', letterSpacing: 1 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 900, color, lineHeight: 1.1 }}>{value}</div>
      {sub2 && <div style={{ fontSize: 13, fontWeight: 700, color: '#64748b' }}>{sub2}</div>}
      <div style={{ fontSize: 11, color: '#334155' }}>{sub}</div>
    </div>
  );
}

function StatusDonut({ projects }) {
  const counts = {};
  let total = 0;
  projects.forEach(p => {
    if (p.type === 'main') return;
    counts[p.status] = (counts[p.status] || 0) + 1;
    total++;
  });
  const statuses = Object.entries(STATUS_CONFIG);
  let cumAngle = -90;
  const R = 60, cx = 80, cy = 80;
  const slices = statuses.map(([key, cfg]) => {
    const count = counts[key] || 0;
    const frac  = total ? count / total : 0;
    const angle = frac * 360;
    const startRad = (cumAngle * Math.PI) / 180;
    const endRad   = ((cumAngle + angle) * Math.PI) / 180;
    const largeArc = angle > 180 ? 1 : 0;
    const x1 = cx + R * Math.cos(startRad);
    const y1 = cy + R * Math.sin(startRad);
    const x2 = cx + R * Math.cos(endRad);
    const y2 = cy + R * Math.sin(endRad);
    const path = frac === 1
      ? `M ${cx} ${cy - R} A ${R} ${R} 0 1 1 ${cx - 0.01} ${cy - R} Z`
      : `M ${cx} ${cy} L ${x1} ${y1} A ${R} ${R} 0 ${largeArc} 1 ${x2} ${y2} Z`;
    cumAngle += angle;
    return { key, cfg, count, path };
  }).filter(s => s.count > 0);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
      <svg width={160} height={160} viewBox="0 0 160 160">
        {slices.map(s => <path key={s.key} d={s.path} fill={s.cfg.color} opacity={0.9} />)}
        <circle cx={cx} cy={cy} r={36} fill="#0f172a" />
        <text x={cx} y={cy - 4} textAnchor="middle" fontSize={18} fontWeight={900} fill="#f1f5f9">{total}</text>
        <text x={cx} y={cy + 14} textAnchor="middle" fontSize={9} fill="#475569" fontWeight={700} letterSpacing={1}>PROJECTS</text>
      </svg>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        {statuses.filter(([k]) => counts[k]).map(([key, cfg]) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ width: 10, height: 10, borderRadius: 2, background: cfg.color, flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: '#94a3b8' }}>{cfg.label}</span>
            <span style={{ fontSize: 14, fontWeight: 800, color: cfg.color, marginLeft: 'auto', minWidth: 20, textAlign: 'right' }}>
              {counts[key] || 0}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function BurnBar({ projects, developers }) {
  const billable = projects.filter(p => p.type !== 'main');
  const sorted   = [...billable].sort((a, b) => getProjectRawShared(b, projects, developers) - getProjectRawShared(a, projects, developers)).slice(0, 8);
  const maxBurn  = getProjectRawShared(sorted[0], projects, developers) || 1;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {sorted.map(p => {
        const burn = getProjectRawShared(p, projects, developers);
        const pct  = (burn / maxBurn) * 100;
        const statusCfg = STATUS_CONFIG[p.status] || STATUS_CONFIG['planned'];
        return (
          <div key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 130, flexShrink: 0, fontSize: 11, color: '#94a3b8', textAlign: 'right',
              whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{p.name}</div>
            <div style={{ flex: 1, height: 16, background: '#1e293b', borderRadius: 4, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${pct}%`, background: statusCfg.color + 'cc', borderRadius: 4, transition: 'width 0.5s' }} />
            </div>
            <div style={{ width: 110, flexShrink: 0, textAlign: 'right' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#fbbf24' }}>{formatCurrency(burn)}</div>
              <div style={{ fontSize: 10, color: '#475569' }}>{formatAED(burn)}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function RecentActivity({ projects }) {
  const recent = [...projects].sort((a, b) => new Date(b.startDate) - new Date(a.startDate)).slice(0, 8);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {recent.map((p, i) => {
        const s = STATUS_CONFIG[p.status] || STATUS_CONFIG['planned'];
        return (
          <div key={p.id} style={{
            display: 'flex', alignItems: 'center', gap: 12,
            padding: '9px 0', borderBottom: i < recent.length - 1 ? '1px solid #0f172a' : 'none',
          }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: s.color, flexShrink: 0, boxShadow: `0 0 4px ${s.color}` }} />
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#e2e8f0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
              <div style={{ fontSize: 10, color: '#334155' }}>Started {formatDate(p.startDate)}</div>
            </div>
            <span style={{ fontSize: 10, fontWeight: 700, color: s.color, flexShrink: 0 }}>{s.label}</span>
          </div>
        );
      })}
    </div>
  );
}

export default function Dashboard({ projects, developers }) {
  const totals     = getPortfolioTotals(projects, developers);
  const billable   = projects.filter(p => p.type !== 'main');
  const activeDevs = developers.filter(d => d.status === 'Active');

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 14 }}>
        <KpiCard icon="📁" label="TOTAL PROJECTS"    value={totals.total}      sub="All types combined"     color="#3b82f6" />
        <KpiCard icon="✅" label="COMPLETED"          value={totals.completed}  sub="Delivered to date"      color="#22c55e" />
        <KpiCard icon="⚡" label="IN PROGRESS"        value={totals.inProgress} sub="Currently active"       color="#3b82f6" />
        <KpiCard icon="📅" label="PLANNED"            value={totals.planned}    sub="Upcoming pipeline"      color="#a855f7" />
        <KpiCard icon="👥" label="ACTIVE TEAM"        value={activeDevs.length} sub={`${formatCurrency(totals.totalMonthlyPayroll)}/mo payroll`} color="#06b6d4" />
        <KpiCard
          icon="💰" label="TOTAL BURN"
          value={formatCurrency(totals.totalOverhead)}
          sub2={formatAED(totals.totalOverhead)}
          sub="With 20% overhead"
          color="#fbbf24"
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 16 }}>
        <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 12, padding: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#94a3b8', letterSpacing: 1, marginBottom: 16 }}>STATUS BREAKDOWN</div>
          <StatusDonut projects={projects} />
        </div>
        <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 12, padding: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#94a3b8', letterSpacing: 1, marginBottom: 16 }}>TOP PROJECTS BY SALARY BURN</div>
          <BurnBar projects={projects} developers={developers} />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
        <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 12, padding: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#94a3b8', letterSpacing: 1, marginBottom: 16 }}>RECENT PROJECTS</div>
          <RecentActivity projects={projects} />
        </div>
        <div style={{ background: '#0f172a', border: '1px solid #1e293b', borderRadius: 12, padding: 20 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#94a3b8', letterSpacing: 1, marginBottom: 16 }}>FINANCIAL SUMMARY</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {[
              ['Monthly Team Payroll',      totals.totalMonthlyPayroll, '#3b82f6'],
              ['Total Raw Salary Burn',     totals.totalRaw,            '#f97316'],
              ['Overhead Buffer (20%)',     totals.totalOverhead - totals.totalRaw, '#f97316'],
              ['Total with Overhead',       totals.totalOverhead,       '#fbbf24'],
              ['Completed Projects Burn',   billable.filter(p => p.status === 'completed').reduce((s, p) => s + getProjectTotalShared(p, projects, developers), 0), '#22c55e'],
              ['In-Progress Burn (to date)',billable.filter(p => p.status === 'in-progress').reduce((s, p) => s + getProjectRawShared(p, projects, developers), 0), '#3b82f6'],
              ['Planned Future Spend',      billable.filter(p => p.status === 'planned').reduce((s, p) => s + getProjectTotalShared(p, projects, developers), 0), '#a855f7'],
            ].map(([label, val, color]) => (
              <div key={label} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '8px 12px', background: '#0a0f1e', borderRadius: 8,
              }}>
                <span style={{ fontSize: 11, color: '#64748b' }}>{label}</span>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color }}>{formatCurrency(val)}</div>
                  <div style={{ fontSize: 10, color: '#475569' }}>{formatAED(val)}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
